import { FormikErrors, FormikProps } from "formik";
import { TFunction } from "i18next";
import React from "react";
import { array, object, string, StringSchema } from "yup";

import {
    AssetPathKey,
    DEFAULT_ASSET_OVERVIEW_FIELDS,
    resolveAssetPathKey,
    SOURCE_FIELD_TO_ASSET_PATH,
} from "domain/asset-management/assetPathMapping";
import {
    ASSET_DETAIL_KEY_MAX_LENGTH,
    ASSET_TYPE_MAX_LENGTH,
    CUSTOM_ASSET_FIELD_MAX_LENGTH,
    INPUT_MAX_LENGTH,
} from "domain/globalConstants";
import { assetService } from "services/asset-management/AssetService";

export interface FormValues {
    assetId: string;
    serialNumber: string;
    assetType: string;
    assetTag: string;
    manufacturer: string;
    model: string;
    assetState: string;
    assignedTo: string;
    location: string;
    department: string;
    region: string;
    parentAssetId: string;
    assetDetails: AssetDetailFormValues[];
}

export interface AssetDetailFormValues {
    key: string;
    value: string;
    isNew?: boolean;
}

export interface Result {
    title: string;
    message: string;
}

const LOCK_STATE = "lock_state";
const HAS_ERASURE_REPORTS = "has_erasure_reports";
const HAS_CHILDREN = "has_children";
const PARENT_UUID = "parent_uuid";
const GROUP_UUIDS = "group_uuids";
const IS_DATA_BEARING = "is_data_bearing";
const IS_TRACKED = "is_tracked";
const DETAILS_TRUNCATED = "details_truncated";

export const NON_EDITABLE_BLANCCO_FIELDS = [
    AssetPathKey.BLANCCO_DURATION,
    AssetPathKey.BLANCCO_ERASABILITY,
    AssetPathKey.BLANCCO_GRADE,
    AssetPathKey.RISK_LEVEL,
    LOCK_STATE,
    HAS_ERASURE_REPORTS,
    HAS_CHILDREN,
    PARENT_UUID,
    GROUP_UUIDS,
    IS_DATA_BEARING,
    IS_TRACKED,
    DETAILS_TRUNCATED,
    AssetPathKey.CREATED_AT,
];

const RESERVED_ASSET_KEYS = [...DEFAULT_ASSET_OVERVIEW_FIELDS, ...NON_EDITABLE_BLANCCO_FIELDS];

export function normalizeAssetKey(key: string): string {
    return (resolveAssetPathKey(key) ?? key).trim().toLowerCase();
}

export function isSameAssetKey(first: string, second: string): boolean {
    return normalizeAssetKey(first) === normalizeAssetKey(second);
}

// Blocks both the raw key (e.g. "asset_state") and its translated label (e.g. "Lifecycle status"),
// since users can type either one when creating a custom field.
export function isReservedAssetKey(value: string, translatedPaths: (key: string) => string): boolean {
    const normalizedValue = value.trim().toLowerCase();
    const resolvedKey = resolveAssetPathKey(value) ?? resolveAssetPathKey(normalizedValue);

    return RESERVED_ASSET_KEYS.some(
        (reservedKey) =>
            reservedKey.toLowerCase() === normalizedValue ||
            translatedPaths(reservedKey).trim().toLowerCase() === normalizedValue ||
            resolvedKey === reservedKey
    );
}

export function isFieldErrors(value: unknown): value is FormikErrors<AssetDetailFormValues> {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const potentialErrors = value as Partial<FormikErrors<AssetDetailFormValues>>;

    return typeof potentialErrors.key === "string" || typeof potentialErrors.value === "string";
}

export const addRow = (formikProps: FormikProps<FormValues>, shouldValidate?: boolean) => {
    const newAdded: AssetDetailFormValues = {
        key: "",
        value: "",
        isNew: true,
    };
    formikProps.setFieldValue("assetDetails", [...formikProps.values.assetDetails, newAdded], shouldValidate);
};

export const notBlank = (errorMessage: string) =>
    ({
        name: "not-blank",
        message: errorMessage,
        test: (value: string | undefined) => !value || value.trim().length > 0,
    }) as const;

const buildFieldSchema = (
    t: TFunction,
    translatedPaths: (key: string) => string,
    pathKey: AssetPathKey,
    translationPrefix: string
) =>
    string()
        .required(t(`${translationPrefix}.fieldMandatoryFieldsErrorMessage`, { field: translatedPaths(pathKey) }))
        .test(notBlank(t(`${translationPrefix}.fieldBlankErrorMessage`, { field: translatedPaths(pathKey) })));

export const buildValidationSchema = (
    t: TFunction,
    translatedPaths: (key: string) => string,
    includeAssetId: boolean
) => {
    const translationPrefix = includeAssetId
        ? "AssetManagement.createAssetSubpage"
        : "AssetManagement.editAssetSubpage";

    const baseFields: Record<string, StringSchema> = {
        serialNumber: buildFieldSchema(t, translatedPaths, AssetPathKey.SERIAL_NUMBER, translationPrefix).max(
            INPUT_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.SERIAL_NUMBER),
                max: INPUT_MAX_LENGTH,
            })
        ),
        assetType: buildFieldSchema(t, translatedPaths, AssetPathKey.ASSET_TYPE, translationPrefix).max(
            ASSET_TYPE_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.ASSET_TYPE),
                max: ASSET_TYPE_MAX_LENGTH,
            })
        ),
        assetTag: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.ASSET_TAG),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        manufacturer: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.MANUFACTURER),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        location: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.LOCATION),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        region: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.REGION),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        department: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.DEPARTMENT),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        assignedTo: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.ASSIGNED_TO),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        assetState: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.ASSET_STATE),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        model: string().max(
            CUSTOM_ASSET_FIELD_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.MODEL),
                max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
            })
        ),
        parentAssetId: string(),
    };

    if (includeAssetId) {
        baseFields.assetId = buildFieldSchema(t, translatedPaths, AssetPathKey.ASSET_ID, translationPrefix).max(
            INPUT_MAX_LENGTH,
            t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                field: translatedPaths(AssetPathKey.ASSET_ID),
                max: INPUT_MAX_LENGTH,
            })
        );
    }

    return object().shape({
        ...baseFields,
        assetDetails: array().of(
            object().shape({
                key: string()
                    .max(
                        ASSET_DETAIL_KEY_MAX_LENGTH,
                        t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                            field: t("AssetManagement.createAssetSubpage.customFields.key"),
                            max: ASSET_DETAIL_KEY_MAX_LENGTH,
                        })
                    )
                    .when("isNew", {
                        is: true,
                        then: (schema: StringSchema) =>
                            schema
                                .required(t("AssetManagement.createAssetSubpage.fieldKeyErrorMessage"))
                                .test(notBlank(t("AssetManagement.createAssetSubpage.fieldKeyErrorMessage")))
                                .test(
                                    "unique-key",
                                    t(
                                        includeAssetId
                                            ? "AssetManagement.createAssetSubpage.duplicateKeyErrorMessage"
                                            : "AssetManagement.createAssetSubpage.duplicateKeyErrorMessage"
                                    ),
                                    function (value) {
                                        if (!value) {
                                            return true;
                                        }
                                        const context = this as typeof this & {
                                            from?: Array<{ value: { assetDetails?: AssetDetailFormValues[] } }>;
                                        };
                                        const assetDetails = context.from?.[1]?.value?.assetDetails;
                                        if (!assetDetails) {
                                            return true;
                                        }
                                        const currentIndexMatch = this.path.match(/assetDetails\[(\d+)\]/);
                                        const currentIndex = currentIndexMatch ? Number(currentIndexMatch[1]) : -1;
                                        if (currentIndex === -1) {
                                            return true;
                                        }
                                        const firstOccurrenceIndex = assetDetails.findIndex((detail) =>
                                            isSameAssetKey(detail.key, value)
                                        );
                                        return currentIndex === firstOccurrenceIndex;
                                    }
                                )
                                .test(
                                    "reserved-key",
                                    t("AssetManagement.createAssetSubpage.reservedKeyErrorMessage"),
                                    (value) => {
                                        if (!value) {
                                            return true;
                                        }
                                        return !isReservedAssetKey(value, translatedPaths);
                                    }
                                ),
                    }),
                value: string()
                    .max(
                        CUSTOM_ASSET_FIELD_MAX_LENGTH,
                        t("AssetManagement.createAssetSubpage.fieldMaxLengthErrorMessage", {
                            field: t("AssetManagement.createAssetSubpage.customFields.value"),
                            max: CUSTOM_ASSET_FIELD_MAX_LENGTH,
                        })
                    )
                    .when("isNew", {
                        is: true,
                        then: (schema: StringSchema) =>
                            schema
                                .required(t("AssetManagement.createAssetSubpage.fieldValueErrorMessage"))
                                .test(notBlank(t("AssetManagement.createAssetSubpage.fieldValueErrorMessage"))),
                    }),
            })
        ),
    });
};

export const useExistingAssetKeys = (): string[] => {
    const [existingKeys, setExistingKeys] = React.useState<string[]>([]);
    React.useEffect(() => {
        const abortController = new AbortController();
        assetService
            .fetchExistingKeys(abortController)
            .then((response) => {
                const sourceFieldKeys = Object.values(SOURCE_FIELD_TO_ASSET_PATH);
                const canonicalResponseKeys = response.keys.map((key) => resolveAssetPathKey(key) ?? key);
                const combinedKeys =
                    canonicalResponseKeys.length > 0 ? [...canonicalResponseKeys, ...sourceFieldKeys] : sourceFieldKeys;
                const uniqueLowercaseKeys = Array.from(new Set(combinedKeys.map((key) => key.toLowerCase())));
                const keys = uniqueLowercaseKeys.filter(
                    (key) =>
                        !DEFAULT_ASSET_OVERVIEW_FIELDS.includes(key as AssetPathKey) &&
                        !NON_EDITABLE_BLANCCO_FIELDS.includes(key as AssetPathKey)
                );
                setExistingKeys(keys);
            })
            .catch((error) => {
                console.error("Failed to fetch existing keys:", error);
            });
        return () => abortController.abort();
    }, []);
    return existingKeys;
};
